import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ShieldCheck, Package, Headset, MapPin, ArrowRight, TestTube,
  CreditCard, FileText, ClipboardText, Truck, Sparkle, Flag as FlagIcon,
} from "@phosphor-icons/react";
import { usePageMeta, SEO_CONSTANTS } from "../lib/usePageMeta";
import { HomeHero } from "../components/home/HomeHero";
import { HomeFeaturedProducts } from "../components/home/HomeFeaturedProducts";
import { HomeCategoryGrid } from "../components/home/HomeCategoryGrid";

// Supported facts only — every line here is defensible from current code
// or explicit policy.  Feb-2026: owner confirmed all peptides are
// manufactured in the United States, so "Peptides made in the USA" is
// now surfaced as the FIRST trust point (see /app/memory/CHANGELOG.md).
// Do NOT extend the claim to raw materials, testing labs, or packaging.
const TRUST_ITEMS = [
  { icon: FlagIcon,   label: "Peptides made in the USA" },
  { icon: MapPin,     label: "U.S.-based company" },
  { icon: CreditCard, label: "Secure Stripe checkout" },
  { icon: TestTube,   label: "Freeze-dried, room-temperature shipping" },
  { icon: Package,    label: "Signature required on delivery" },
  { icon: Headset,    label: "Real people answer support" },
];

// "Why Medical Peptide Store" — five short, honest promises.
const WHY_MPS = [
  {
    icon: FileText,
    title: "Clear batch documents",
    body: "We publish documents for each specific batch in the COA Library when they’re ready. If a document isn’t published yet, ask us and we’ll share what’s available for that batch.",
  },
  {
    icon: ClipboardText,
    title: "Batch-tracked inventory",
    body: "Every box is packed against a specific batch, with the batch number on the packing list so the paperwork you receive lines up with the vial you get.",
  },
  {
    icon: CreditCard,
    title: "Secure direct purchase",
    body: "You pay through Stripe. Prices are checked securely on our side before payment — no browser-set prices, no third-party marketplaces, no surprise fees.",
  },
  {
    icon: Truck,
    title: "Tracked, signature delivery",
    body: "Live shipping rates are calculated at checkout. Every package ships fully tracked and needs a signature at the receiving address on delivery.",
  },
  {
    icon: Headset,
    title: "Human support",
    body: "Questions about a batch, a shipment, or a policy? A real person answers info@medicalpeptidestore.com during our published support hours.",
  },
];

const HOW_STEPS = [
  {
    n: "01",
    title: "Choose a research peptide",
    body: "Search by compound name or browse by research area. Every listing shows real stock and batch-tracked availability.",
  },
  {
    n: "02",
    title: "Review your cart and terms",
    body: "The cart shows the items, quantities, and research-use notice before you go to Stripe. The browser never sets any prices.",
  },
  {
    n: "03",
    title: "Continue to secure checkout",
    body: "Shipping is calculated live from your address and the carrier you pick. Every order is tracked and requires a signature.",
  },
];

// Homepage FAQ preview — six operational questions.  Full FAQ has more.
const HOME_FAQ = [
  {
    q: "How are the peptides shipped?",
    a: "Freeze-dried research peptides ship at room temperature in tamper-evident boxes. Every shipment is tracked and needs a signature at delivery.",
  },
  {
    q: "How is shipping calculated?",
    a: "We show live shipping rates from the carriers at checkout, based on your address and the service you pick. No flat fees, no hidden markups.",
  },
  {
    q: "Is a signature required on delivery?",
    a: "Yes. Every order ships with signature-required service and full tracking from the label being made through the door.",
  },
  {
    q: "Where do batch documents live?",
    a: "We publish documents in the COA Library once a batch has been reviewed. If the document you want isn’t published yet, email us the batch number and we’ll share what’s available.",
  },
  {
    q: "Are products for human or animal use?",
    a: "No. These are research peptides sold strictly for laboratory research. They are not intended for human or animal use, diagnosis, or treatment.",
  },
  {
    q: "Do I need an account to order?",
    a: "No — you can check out as a guest. If you make an optional account, you can see your past orders and batch numbers in one place.",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCompounds, setTotalCompounds] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data.items));
    // Fetch true total for JSON-LD and downstream copy (never invented).
    api.get("/products", { params: { limit: 1 } }).then((r) => setTotalCompounds(r.data.total));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  usePageMeta({
    title: "Premium Peptide Reference Standards",
    description:
      "Browse 105 research reference standards with all peptides manufactured in the United States. Lot-tracked, signature-required delivery. Research use only.",
    canonicalPath: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": `${SEO_CONSTANTS.PROD_HOST}/#organization`,
        "name": "Medical Peptide Store",
        "url": SEO_CONSTANTS.PROD_HOST,
        "description": "U.S.-based supplier of research reference standards. All peptides manufactured in the United States. For research use only.",
        "email": "info@medicalpeptidestore.com",
        "telephone": "+1-888-294-7717",
        "address": { "@type": "PostalAddress", "addressCountry": "US" },
        "logo": `${SEO_CONSTANTS.PROD_HOST}/logo512.png`,
        "contactPoint": [{
          "@type": "ContactPoint",
          "email": "info@medicalpeptidestore.com",
          "telephone": "+1-888-294-7717",
          "contactType": "customer service",
          "areaServed": "US",
          "availableLanguage": ["English"],
        }],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SEO_CONSTANTS.PROD_HOST}/#website`,
        "name": "Medical Peptide Store",
        "url": SEO_CONSTANTS.PROD_HOST,
        "publisher": { "@id": `${SEO_CONSTANTS.PROD_HOST}/#organization` },
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${SEO_CONSTANTS.PROD_HOST}/shop?search={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${SEO_CONSTANTS.PROD_HOST}/#home-faq`,
        "mainEntity": HOME_FAQ.map(({ q, a }) => ({
          "@type": "Question",
          "name": q,
          "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
      },
      ...(featured.length > 0 ? [{
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Featured research reference standards",
        "itemListOrder": "https://schema.org/ItemListOrderAscending",
        "numberOfItems": featured.length,
        "itemListElement": featured.map((p, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "url": `${SEO_CONSTANTS.PROD_HOST}/product/${p.slug}`,
          "name": p.product_name,
        })),
      }] : []),
    ],
  });

  return (
    <div data-testid="home-page" className="bg-white text-brand-text">
      {/* 3. Product-led hero */}
      <HomeHero />

      {/* 4. Trust strip — supported facts only */}
      <section
        className="border-y border-brand-border bg-brand-surface"
        data-testid="home-truststrip"
      >
        <div className="mps-container py-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            {TRUST_ITEMS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 rounded-full bg-white border border-brand-border flex items-center justify-center">
                  <Icon size={18} weight="regular" className="text-brand-navy" aria-hidden />
                </div>
                <div className="text-[13px] font-medium text-brand-text leading-tight">
                  {label}
                </div>
              </div>
            ))}
          </div>
          {/*
            Research-use disclosure — small but readable footnote
            directly below the trust strip. Kept above the fold on
            mobile so the compliance context is always visible without
            competing with the retail hero for attention.
          */}
          <p
            data-testid="home-research-use-footnote"
            role="note"
            aria-label="Research use disclaimer"
            className="mt-6 pt-6 border-t border-brand-border/60 text-xs text-brand-muted leading-relaxed max-w-3xl"
          >
            Sold for laboratory research only. Not for human or veterinary use. Not intended to
            diagnose, treat, cure, or prevent any disease. See our{" "}
            <Link
              to="/research-use-policy"
              className="underline underline-offset-2 hover:text-brand-navy"
              data-testid="home-research-use-policy-link"
            >
              Research-Use Policy
            </Link>{" "}
            for details.
          </p>
        </div>
      </section>

      {/* 5. Shop Best Sellers — reuses existing card component */}
      <HomeFeaturedProducts featured={featured} totalCompounds={totalCompounds} />

      {/* 6. Why Medical Peptide Store */}
      <section
        className="mps-container mps-section"
        data-testid="home-why"
      >
        <div className="max-w-3xl mb-14">
          <div className="eyebrow mb-4">Why Medical Peptide Store</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
            Clear documents, tracked delivery, real support.
          </h2>
          <p className="mt-5 text-lg text-brand-muted leading-relaxed max-w-2xl">
            No marketing gloss — just the facts every researcher wants to
            see before they hit checkout.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {WHY_MPS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              data-testid={`why-item-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="mps-card p-7 lg:p-8"
            >
              <div className="h-11 w-11 rounded-[12px] bg-brand-surface border border-brand-border flex items-center justify-center mb-5">
                <Icon size={20} weight="regular" className="text-brand-navy" aria-hidden />
              </div>
              <h3 className="font-display text-lg md:text-xl font-semibold text-brand-navy leading-snug tracking-tight mb-2">
                {title}
              </h3>
              <p className="text-brand-muted text-[15px] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 7. How ordering works */}
      <section
        id="how-it-works"
        tabIndex={-1}
        className="bg-brand-navy text-white scroll-mt-[140px] md:scroll-mt-[160px] focus:outline-none"
        data-testid="home-how"
      >
        <div className="mps-container mps-section">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-accent mb-4">
              How ordering works
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight !text-white leading-tight">
              Three steps, no gimmicks.
            </h2>
            <p className="mt-5 text-lg text-white/75 leading-relaxed max-w-2xl">
              Every order goes through Stripe. Prices are checked securely
              before payment, shipping is quoted live from the carriers, and
              every package needs a signature at delivery.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {HOW_STEPS.map(({ n, title, body }) => (
              <div
                key={n}
                data-testid={`how-step-${n}`}
                className="rounded-[16px] bg-white/5 border border-white/10 backdrop-blur-sm p-7 lg:p-8"
              >
                <div className="font-mono text-brand-accent text-[13px] font-semibold tracking-[0.24em] mb-6">
                  Step {n}
                </div>
                <h3 className="font-display text-xl md:text-2xl font-semibold !text-white leading-snug tracking-tight mb-3">
                  {title}
                </h3>
                <p className="text-white/75 text-[15px] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Documentation / COA truthful summary */}
      <section
        className="mps-container mps-section"
        data-testid="home-docs"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Documents</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
              Batch documents, published once they’re reviewed.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-brand-muted">
            <p className="text-lg text-brand-text leading-relaxed">
              We don’t show generic or placeholder documents. What you see
              for a batch is what we can back up.
            </p>
            <ul className="space-y-4 text-[15px] leading-relaxed">
              <li className="flex gap-3">
                <ShieldCheck size={18} weight="regular" className="text-brand-accent shrink-0 mt-1" aria-hidden />
                <span>
                  <strong className="text-brand-navy">Published batches.</strong>{" "}
                  Once a batch is reviewed and ready, the document appears in the
                  COA Library referenced by the batch number on your packing list.
                </span>
              </li>
              <li className="flex gap-3">
                <FileText size={18} weight="regular" className="text-brand-accent shrink-0 mt-1" aria-hidden />
                <span>
                  <strong className="text-brand-navy">Documents by request.</strong>{" "}
                  If your document isn’t published yet, email
                  {" "}<a href="mailto:info@medicalpeptidestore.com" className="text-brand-accent hover:underline">info@medicalpeptidestore.com</a>{" "}
                  with the batch number and we’ll share what’s available.
                </span>
              </li>
              <li className="flex gap-3">
                <ClipboardText size={18} weight="regular" className="text-brand-accent shrink-0 mt-1" aria-hidden />
                <span>
                  <strong className="text-brand-navy">What ships in every box.</strong>{" "}
                  A packing list, the research-use notice, and any batch
                  document that has been reviewed and published at ship time.
                </span>
              </li>
            </ul>
            <div className="pt-2">
              <Link
                to="/coa-library"
                data-testid="home-docs-cta"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-accent hover:text-brand-navy transition-colors"
              >
                Visit the COA Library <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories — plain-English research-area grid */}
      <HomeCategoryGrid />

      {/* 9. Homepage FAQ preview */}
      <section
        className="bg-brand-surface"
        data-testid="home-faq"
      >
        <div className="mps-container mps-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-4">FAQ</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
                Common questions, straight answers.
              </h2>
              <p className="mt-5 text-brand-muted leading-relaxed">
                Six of the questions we hear most often. The full FAQ
                covers products, ordering, shipping, storage, and support.
              </p>
              <Link
                to="/faq"
                data-testid="home-faq-cta"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-accent hover:text-brand-navy transition-colors"
              >
                View the full FAQ <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="lg:col-span-8 divide-y divide-brand-border rounded-[14px] bg-white border border-brand-border overflow-hidden">
              {HOME_FAQ.map(({ q, a }, i) => (
                <details
                  key={q}
                  className="group"
                  data-testid={`home-faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4 hover:bg-brand-surface transition-colors">
                    <span className="font-display text-brand-navy text-base font-medium leading-snug">
                      {q}
                    </span>
                    <span className="shrink-0 h-7 w-7 rounded-full bg-brand-surface border border-brand-border flex items-center justify-center text-brand-navy font-semibold group-open:bg-brand-accent group-open:text-white group-open:border-brand-accent transition-colors">
                      <span className="group-open:hidden">+</span>
                      <span className="hidden group-open:inline">–</span>
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-brand-muted text-[15px] leading-relaxed">
                    {a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 10. Closing catalog CTA */}
      <section
        className="mps-container mps-section text-center"
        data-testid="home-final-cta"
      >
        <div className="max-w-2xl mx-auto">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-brand-surface border border-brand-border mb-6">
            <Sparkle size={22} weight="regular" className="text-brand-accent" aria-hidden />
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
            Ready to shop research peptides?
          </h2>
          <p className="mt-5 text-lg text-brand-muted leading-relaxed">
            Browse the full catalog and check out securely. Every order
            ships tracked and needs a signature at delivery.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/shop"
              data-testid="home-final-shop-cta"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-[14px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold shadow-button transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-[1px] hover:shadow-soft"
            >
              Browse the Catalog <ArrowRight size={16} weight="bold" />
            </Link>
            <Link
              to="/contact"
              data-testid="home-final-contact-cta"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-[14px] bg-white text-brand-navy border border-brand-navy hover:bg-brand-surface font-semibold transition-colors duration-200"
            >
              Contact us
            </Link>
          </div>
          <p className="mt-8 text-[11px] text-brand-muted max-w-md mx-auto leading-relaxed">
            All items are research peptides sold strictly for laboratory
            research. Not for human or animal use.
          </p>
        </div>
      </section>
    </div>
  );
}
